import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  CheckCircle2, Trophy, RefreshCw, Sparkles, Loader2, AlertCircle, History,
} from "lucide-react";
import {
  getDashboardStats, getPaymentStatsByStructure,
  autoMatchTransactions, manualSync,
} from "@/lib/api";
import { toast } from "sonner";

const MONTHS_FR = ["janvier", "février", "mars", "avril", "mai", "juin",
  "juillet", "août", "septembre", "octobre", "novembre", "décembre"];

function fmt(amount) {
  return new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(amount || 0);
}

// Couleur selon le taux de paiement (classement)
function rateColor(pct) {
  if (pct >= 80) return { bar: "from-navy-500 to-navy-600", text: "text-navy-700" };
  if (pct >= 50) return { bar: "from-amber-400 to-amber-500", text: "text-amber-600" };
  return { bar: "from-red-400 to-red-500", text: "text-red-600" };
}

// Section "qui n'a pas payé", groupé par structure
function UnpaidByStructure({ data, title, icon: Icon, accent }) {
  const structuresWithUnpaid = (data?.by_structure || [])
    .filter(s => (s.unpaid_tenants || []).length > 0)
    .sort((a, b) => b.unpaid_tenants.length - a.unpaid_tenants.length);

  const totalUnpaid = structuresWithUnpaid.reduce((s, st) => s + st.unpaid_tenants.length, 0);

  if (!data) return null;

  return (
    <Card className={`border-0 shadow-sm ${accent === "orange" ? "ring-1 ring-orange-100" : ""}`}>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base" style={{ fontFamily: "Manrope" }}>
          <Icon className={`w-4 h-4 ${accent === "orange" ? "text-orange-700" : "text-slate-500"}`} />
          {title}
          {totalUnpaid > 0 && (
            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${accent === "orange" ? "bg-orange-100 text-orange-700" : "bg-slate-100 text-slate-600"}`}>
              {totalUnpaid}
            </span>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {totalUnpaid === 0 ? (
          <div className="flex items-center gap-2 text-sm text-navy-700">
            <CheckCircle2 className="w-4 h-4" /> Tout le monde a payé 🎉
          </div>
        ) : (
          structuresWithUnpaid.map((structure, idx) => (
            <div key={idx}>
              <div className="flex items-center justify-between mb-1.5">
                <h4 className="font-semibold text-slate-800 text-sm">{structure.name}</h4>
                <span className="text-xs text-slate-400">{structure.unpaid_tenants.length} impayé{structure.unpaid_tenants.length > 1 ? "s" : ""}</span>
              </div>
              <div className="space-y-1">
                {structure.unpaid_tenants.map((t) => (
                  <div key={t.id || t.name} className={`flex items-center justify-between px-3 py-2 rounded-lg ${accent === "orange" ? "bg-orange-50" : "bg-slate-50"}`}>
                    <div className="flex items-center gap-2 min-w-0">
                      <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold shrink-0 ${accent === "orange" ? "bg-orange-200 text-orange-800" : "bg-slate-200 text-slate-700"}`}>
                        {t.name.charAt(0).toUpperCase()}
                      </span>
                      <span className="text-sm text-slate-800 truncate">{t.name}</span>
                    </div>
                    <span className={`text-sm font-semibold shrink-0 ml-2 ${accent === "orange" ? "text-orange-700" : "text-slate-700"}`}>
                      {fmt(t.rent_amount)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))
        )}
      </CardContent>
    </Card>
  );
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [structureStats, setStructureStats] = useState(null);
  const [prevStats, setPrevStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [matching, setMatching] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const now = new Date();
      const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const [statsRes, structureRes, prevRes] = await Promise.all([
        getDashboardStats(),
        getPaymentStatsByStructure(),
        getPaymentStatsByStructure(prev.getMonth() + 1, prev.getFullYear()),
      ]);
      setStats(statsRes.data);
      setStructureStats(structureRes.data);
      setPrevStats(prevRes.data);
    } catch (error) {
      toast.error("Erreur lors du chargement des données");
    } finally {
      setLoading(false);
    }
  };

  const handleAutoMatch = async () => {
    setMatching(true);
    try {
      const response = await autoMatchTransactions();
      const { matches } = response.data;
      if (matches.length > 0) {
        toast.success(`${matches.length} paiement(s) associé(s) automatiquement !`);
        fetchData();
      } else {
        toast.info("Aucune nouvelle correspondance trouvée");
      }
    } catch (error) {
      toast.error("Erreur lors du matching automatique");
    } finally {
      setMatching(false);
    }
  };

  const handleManualSync = async () => {
    setSyncing(true);
    try {
      const response = await manualSync();
      const results = response.data.results;
      let message = "Synchronisation terminée : ";
      if (results.notion_sync?.success) message += `${results.notion_sync.count} locataires`;
      if (results.bank_sync?.success) message += `, ${results.bank_sync.count} transactions`;
      if (results.matching?.success) message += `, ${results.matching.count} matchés`;
      toast.success(message);
      if (results.bank_sync?.skipped?.length > 0) {
        toast.warning(
          `Comptes non synchronisés (aucune banque liée) : ${results.bank_sync.skipped.join(", ")}. Liez-les depuis la page Banques.`
        );
      }
      fetchData();
    } catch (error) {
      toast.error("Erreur lors de la synchronisation");
    } finally {
      setSyncing(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-navy-900" />
      </div>
    );
  }

  const currentMonthLabel = MONTHS_FR[new Date().getMonth()];
  const prevDate = new Date(new Date().getFullYear(), new Date().getMonth() - 1, 1);
  const prevMonthLabel = MONTHS_FR[prevDate.getMonth()];
  const overall = structureStats?.overall;
  const ranked = [...(structureStats?.by_structure || [])].sort((a, b) => b.percentage - a.percentage);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="dashboard-page">

      {/* ── HEADER ── */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: "Manrope" }}>
            Suivi des paiements
          </h1>
          <p className="text-sm text-slate-500 mt-1 capitalize">{currentMonthLabel} {new Date().getFullYear()} · toutes structures</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={handleManualSync} disabled={syncing} variant="outline" size="sm" className="gap-2 text-sm">
            {syncing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            <span className="hidden sm:inline">{syncing ? "Synchronisation..." : "Synchroniser"}</span>
            <span className="sm:hidden">Sync</span>
          </Button>
          <Button onClick={handleAutoMatch} disabled={matching} size="sm" className="bg-navy-900 hover:bg-navy-800 gap-2 text-sm">
            {matching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            <span className="hidden sm:inline">{matching ? "Matching..." : "Associer paiements"}</span>
            <span className="sm:hidden">Associer</span>
          </Button>
        </div>
      </div>

      {/* ── KPIs PAIEMENTS ── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Card className="border-0 shadow-sm overflow-hidden">
          <div className="h-1 bg-slate-400" />
          <CardContent className="p-4">
            <p className="text-xs text-slate-500 font-medium uppercase tracking-wide">Locataires actifs</p>
            <p className="text-3xl font-bold text-slate-900 mt-1 leading-none" style={{ fontFamily: "Manrope" }}>{stats?.total_tenants || 0}</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm overflow-hidden">
          <div className="h-1 bg-navy-500" />
          <CardContent className="p-4">
            <p className="text-xs text-slate-500 font-medium uppercase tracking-wide">Payés ce mois</p>
            <p className="text-3xl font-bold text-navy-600 mt-1 leading-none" style={{ fontFamily: "Manrope" }}>{stats?.paid_tenants || 0}</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm overflow-hidden">
          <div className="h-1 bg-orange-400" />
          <CardContent className="p-4">
            <p className="text-xs text-slate-500 font-medium uppercase tracking-wide">Impayés ce mois</p>
            <p className="text-3xl font-bold text-orange-700 mt-1 leading-none" style={{ fontFamily: "Manrope" }}>{stats?.unpaid_tenants || 0}</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm overflow-hidden">
          <div className="h-1 bg-navy-600" />
          <CardContent className="p-4">
            <p className="text-xs text-slate-500 font-medium uppercase tracking-wide">Collecté ce mois</p>
            <p className="text-xl font-bold text-navy-700 mt-1 leading-none" style={{ fontFamily: "Manrope" }}>
              {fmt(stats?.total_rent_collected)}
            </p>
            <p className="text-xs text-slate-400 mt-0.5">/ {fmt(stats?.total_rent_expected)} attendus</p>
          </CardContent>
        </Card>
      </div>

      {/* ── PROGRESSION GLOBALE ── */}
      {overall && (
        <Card className="border-0 shadow-sm">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-navy-600" />
                <span className="text-sm font-semibold text-slate-700">Progression globale du mois</span>
              </div>
              <span className="text-2xl font-bold text-navy-700" style={{ fontFamily: "Manrope" }}>{overall.percentage}%</span>
            </div>
            <div className="w-full bg-slate-100 rounded-full h-4 overflow-hidden">
              <div
                className="h-4 rounded-full bg-gradient-to-r from-navy-500 to-navy-600 transition-all duration-700"
                style={{ width: `${Math.max(overall.percentage, 2)}%` }}
              />
            </div>
            <div className="flex items-center justify-between text-xs mt-2">
              <span className="text-slate-500">{overall.paid} / {overall.total} locataires à jour</span>
              <span className="text-orange-600 font-medium">{fmt(overall.unpaid_amount)} restants à encaisser</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ── CLASSEMENT PAR STRUCTURE ── */}
      {ranked.length > 0 && (
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base" style={{ fontFamily: "Manrope" }}>
              <Trophy className="w-4 h-4 text-amber-500" />
              Classement des structures
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            {ranked.map((structure, idx) => {
              const c = rateColor(structure.percentage);
              const remaining = (structure.expected_amount || 0) - (structure.paid_amount || 0);
              return (
                <div key={idx} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="w-6 h-6 rounded-full bg-slate-100 text-slate-500 text-xs font-bold flex items-center justify-center shrink-0">
                        {idx + 1}
                      </span>
                      <div className="min-w-0">
                        <h4 className="font-semibold text-slate-900 text-sm truncate">{structure.name}</h4>
                        <p className="text-xs text-slate-500">{structure.paid} / {structure.total} locataires payés</p>
                      </div>
                    </div>
                    <span className={`text-lg font-bold ${c.text} shrink-0 ml-2`} style={{ fontFamily: "Manrope" }}>{structure.percentage}%</span>
                  </div>
                  <div className="w-full bg-slate-100 rounded-full h-3 overflow-hidden">
                    <div
                      className={`h-3 rounded-full bg-gradient-to-r ${c.bar} transition-all duration-500`}
                      style={{ width: `${Math.max(structure.percentage, 2)}%` }}
                    />
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-navy-700 font-medium">{fmt(structure.paid_amount)} collectés</span>
                    {remaining > 0
                      ? <span className="text-orange-600 font-medium">{fmt(remaining)} restants</span>
                      : <span className="text-navy-600 font-medium flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Complet</span>}
                  </div>
                </div>
              );
            })}
            <div className="flex items-center justify-center gap-6 pt-2 text-xs text-slate-400 border-t border-slate-100">
              <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-navy-500 inline-block" /> ≥ 80%</span>
              <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-amber-500 inline-block" /> 50–79%</span>
              <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-red-500 inline-block" /> &lt; 50%</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ── QUI N'A PAS PAYÉ (ce mois + mois précédent) ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <UnpaidByStructure
          data={structureStats}
          title={`Impayés de ${currentMonthLabel}`}
          icon={AlertCircle}
          accent="orange"
        />
        <UnpaidByStructure
          data={prevStats}
          title={`Impayés de ${prevMonthLabel} (à relancer)`}
          icon={History}
          accent="slate"
        />
      </div>

    </div>
  );
}
